import { Component, Input, Inject, NgZone, OnDestroy, PLATFORM_ID, ChangeDetectorRef, forwardRef } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { DashboardBaseChart } from '../shared/dashboard-base-chart';
import { DashboardBaseComponent } from '../shared/dashboard-base-component';
import {ChartLoadingComponent} from "../chart-loading/chart-loading.component";

export interface ChartLineSeries {
  field: string;
  name: string;
  color: string;
  dashed?: boolean;
}

export interface ChartLineConfig {
  chartId: string;
  categoryField: string;
  series: ChartLineSeries[];
  data: any[];
  yLabelFormatter?: (value: number) => string;
  xLabelRotation?: number;       // e.g. -45 for angled labels
}

export interface ChartLineCardConfig {
  title: string;
  sub?: string;
  chart: ChartLineConfig;
}

@Component({
  selector: 'app-chart-line',
  templateUrl: './chart-line.component.html',
  styleUrls: ['./chart-line.component.scss'],
  standalone: true,
  imports: [
    ChartLoadingComponent
  ],
  providers: [
    {
      provide: DashboardBaseComponent,
      useExisting: forwardRef(() => ChartLineComponent)
    }
  ]
})
export class ChartLineComponent extends DashboardBaseChart implements OnDestroy {
  @Input() config!: ChartLineConfig;

  isLoading = true;

  private xAxis?: am5xy.CategoryAxis<am5xy.AxisRenderer>;
  private lineSeriesList: am5xy.LineSeries[] = [];

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  protected initChart(): void {
    if (!isPlatformBrowser(this.platformId) || !this.config.chartId) return;

    this.zone.runOutsideAngular(() => {
      const root = am5.Root.new(this.config.chartId);
      root.setThemes([am5themes_Animated.new(root)]);
      if (root._logo) root._logo.dispose();
      this.root = root;

      const textColor  = am5.color(0xd9e8ff);
      const mutedColor = am5.color(0x9db8d7);
      const gridColor  = am5.color(0x33506f);

      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          paddingLeft: 0,
        })
      );

      // X axis
      const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: this.config.categoryField,
          renderer: am5xy.AxisRendererX.new(root, { minGridDistance: 25 }),
        })
      );
      this.xAxis = xAxis;
      const xRotation = this.config.xLabelRotation;
      xAxis.get('renderer').labels.template.setAll({
        fill: mutedColor,
        fontSize: 11,
        ...(xRotation !== undefined ? {
          rotation: xRotation,
          centerX: am5.percent(100),
          centerY: am5.percent(50),
        } : {}),
      });
      xAxis.get('renderer').grid.template.setAll({ stroke: gridColor, strokeOpacity: 0.35 });

      // Y axis
      const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: am5xy.AxisRendererY.new(root, {}),
        })
      );
      yAxis.get('renderer').labels.template.setAll({ fill: mutedColor, fontSize: 11 });
      yAxis.get('renderer').grid.template.setAll({ stroke: gridColor, strokeOpacity: 0.35 });
      if (this.config.yLabelFormatter) {
        const fmt = this.config.yLabelFormatter;
        yAxis.get('renderer').labels.template.adapters.add('text', (_text, target) => {
          const value = (target.dataItem as any)?.get('value') as number | undefined;
          return value != null ? fmt(value) : _text;
        });
      }

      // Line series
      for (const s of this.config.series) {
        const lineColor = am5.color(s.color);
        const series = chart.series.push(
          am5xy.LineSeries.new(root, {
            name: s.name,
            xAxis,
            yAxis,
            categoryXField: this.config.categoryField,
            valueYField: s.field,
            stroke: lineColor,
            connect: false,
            tooltip: am5.Tooltip.new(root, {
              labelText: `[bold]{name}[/]\n{categoryX}: {valueY}M`,
            }),
          })
        );

        series.strokes.template.setAll({
          strokeWidth: 3,
          strokeDasharray: s.dashed ? [5, 4] : [],
        });

        series.bullets.push(() =>
          am5.Bullet.new(root, {
            sprite: am5.Circle.new(root, { radius: 4, fill: lineColor }),
          })
        );

        series.appear(500);
        this.lineSeriesList.push(series);
      }

      // Legend
      const legend = chart.children.push(
        am5.Legend.new(root, {
          x: am5.percent(50),
          centerX: am5.percent(50),
          y: 0,
          centerY: 0,
        })
      );
      legend.labels.template.setAll({ fill: textColor, fontSize: 12 });
      legend.valueLabels.template.setAll({ forceHidden: true });
      legend.data.setAll(chart.series.values);

      chart.set('cursor', am5xy.XYCursor.new(root, { behavior: 'none' }));
      chart.appear(500, 100);
    });
  }

  /** single shared dataset */
  load(data: any[]): void;
  /** per-series datasets — each series gets its own array; xAxis uses datasets[0] unless xAxisData is provided */
  load(datasets: any[][], xAxisData?: any[]): void;
  load(data: any[] | any[][], xAxisData?: any[]): void {
    this.isLoading = false;
    this.cdr.detectChanges();
    if (!this.root) this.initChart();

    const isPerSeries = data.length > 0 && Array.isArray(data[0]);
    const axisData = xAxisData ?? (isPerSeries ? (data as any[][])[0] : data as any[]) ?? [];
    this.config.data = axisData;
    this.xAxis?.data.setAll(axisData);
    this.lineSeriesList.forEach((s, i) =>
      s.data.setAll(isPerSeries ? ((data as any[][])[i] ?? axisData) : data as any[])
    );
    this.isLoaded = true;
  }

  ngOnDestroy(): void {
    this.lineSeriesList = [];
    this.root?.dispose();
  }
}
